import { api, fetchWithETag } from '@/lib/api'
import { MAX_MAP_POINTS, MAX_PORTFOLIO_MAP_POINTS } from '@/lib/constants'
import type {
  Portfolio,
  PortfolioListItem,
  PortfolioAsset,
  PortfolioAssetListItem,
  PortfolioCreateRequest,
  PortfolioValuationUpdate,
  PortfolioStatistics,
  PortfolioRiskExport,
  GeoJSONFeatureCollection,
  RiskSummary,
  PaginatedResponse,
  PortfolioInput,
} from '@/types/api'

// =============================================================================
// PORTFOLIO SERVICE (Parent Container)
// =============================================================================

export interface PortfolioFilters {
  portfolio_type?: string
  country_code?: string
  /** Comma-separated country codes for filtering (e.g., "VN,PH") */
  countries?: string
  search?: string
  ordering?: string
  page?: number
}

export const portfolioService = {
  /**
   * Get paginated list of portfolios
   */
  async getAll(filters?: PortfolioFilters): Promise<PaginatedResponse<PortfolioListItem>> {
    const params = new URLSearchParams()
    if (filters?.portfolio_type) params.set('portfolio_type', filters.portfolio_type)
    if (filters?.country_code) params.set('country_code', filters.country_code)
    if (filters?.countries) params.set('countries', filters.countries)
    if (filters?.search) params.set('search', filters.search)
    if (filters?.ordering) params.set('ordering', filters.ordering)
    if (filters?.page) params.set('page', String(filters.page))

    const query = params.toString()
    return api.get<PaginatedResponse<PortfolioListItem>>(
      `/portfolios/${query ? `?${query}` : ''}`
    )
  },

  /**
   * Get single portfolio with full details and nested assets
   */
  async getById(id: number): Promise<Portfolio> {
    return api.get<Portfolio>(`/portfolios/${id}/`)
  },

  /**
   * Get portfolio as GeoJSON (all assets in this portfolio)
   * Uses ETag caching for performance
   * Applies sampling when asset count exceeds limit
   */
  async getGeoJSON(id: number): Promise<GeoJSONFeatureCollection & { portfolio: { id: number; name: string; asset_count: number } }> {
    return fetchWithETag(`/portfolios/${id}/geojson/?limit=${MAX_PORTFOLIO_MAP_POINTS}`)
  },

  /**
   * Get assets in a portfolio with optional limit
   * Applies sampling when portfolio exceeds limit
   */
  async getAssets(id: number, limit?: number): Promise<{
    count: number
    portfolio_id: number
    portfolio_name: string
    results: PortfolioAssetListItem[]
  }> {
    const limitParam = limit ? `?limit=${limit}` : `?limit=${MAX_PORTFOLIO_MAP_POINTS}`
    return api.get(`/portfolios/${id}/assets/${limitParam}`)
  },

  /**
   * Get portfolio statistics summary
   */
  async getStatistics(): Promise<{
    total_portfolios: number
    total_assets: number
    by_type: Array<{ portfolio_type: string; count: number }>
    by_country: Array<{ country_code: string; count: number }>
  }> {
    return api.get('/portfolios/statistics/')
  },
  /**
   * Create new portfolio container
   */
  async create(data: PortfolioInput): Promise<Portfolio> {
    return api.post<Portfolio>('/portfolios/', data)
  },
}

// =============================================================================
// ASSET SERVICE (Individual Locations)
// =============================================================================

export interface AssetFilters {
  portfolio?: number
  category?: string
  country_code?: string
  business_unit?: string
  basement_code?: string
  value_currency?: string
  search?: string
  ordering?: string
  page?: number
}

export interface NearbySearchParams {
  lat: number
  lon: number
  radius?: number
}

export interface NearbySearchResponse {
  count: number
  center: { lat: number; lon: number }
  radius_km: number
  results: PortfolioAssetListItem[]
}

export interface CountryInfo {
  code: string
  name: string
  count: number
}

export interface AvailableCountriesResponse {
  countries: CountryInfo[]
  default_country: string
  total_assets: number
}

export const assetService = {
  /**
   * Get paginated list of assets
   */
  async getAll(filters?: AssetFilters): Promise<PaginatedResponse<PortfolioAssetListItem>> {
    const params = new URLSearchParams()
    if (filters?.portfolio) params.set('portfolio', String(filters.portfolio))
    if (filters?.category) params.set('category', filters.category)
    if (filters?.country_code) params.set('country_code', filters.country_code)
    if (filters?.business_unit) params.set('business_unit', filters.business_unit)
    if (filters?.basement_code) params.set('basement_code', filters.basement_code)
    if (filters?.value_currency) params.set('value_currency', filters.value_currency)
    if (filters?.search) params.set('search', filters.search)
    if (filters?.ordering) params.set('ordering', filters.ordering)
    if (filters?.page) params.set('page', String(filters.page))

    const query = params.toString()
    return api.get<PaginatedResponse<PortfolioAssetListItem>>(
      `/assets/${query ? `?${query}` : ''}`
    )
  },

  /**
   * Get single asset with full details
   */
  async getById(id: number): Promise<PortfolioAsset> {
    return api.get<PortfolioAsset>(`/assets/${id}/`)
  },

  /**
   * Create new asset
   */
  async create(data: PortfolioCreateRequest & { portfolio?: number }): Promise<PortfolioAsset> {
    return api.post<PortfolioAsset>('/assets/', data)
  },

  /**
   * Update asset
   */
  async update(id: number, data: Partial<PortfolioCreateRequest>): Promise<PortfolioAsset> {
    return api.patch<PortfolioAsset>(`/assets/${id}/`, data)
  },

  /**
   * Delete asset
   */
  async delete(id: number): Promise<void> {
    return api.delete(`/assets/${id}/`)
  },

  /**
   * Update valuation fields only
   */
  async updateValuation(id: number, data: PortfolioValuationUpdate): Promise<{
    id: number
    name: string
    building_value: number
    contents_value: number
    inventory_value: number
    total_value: number
    value_currency: string
  }> {
    return api.patch(`/assets/${id}/valuation/`, data)
  },

  /**
   * Get all assets as GeoJSON for map display
   * Uses ETag caching for performance
   * @param countries Optional comma-separated country codes to filter
   */
  async getGeoJSON(countries?: string): Promise<GeoJSONFeatureCollection> {
    const params = new URLSearchParams()
    if (countries) params.append('countries', countries)
    params.append('limit', String(MAX_MAP_POINTS))

    return fetchWithETag<GeoJSONFeatureCollection>(`/assets/geojson/?${params.toString()}`)
  },

  /**
   * Get available countries with asset counts
   * Results are role-filtered by backend
   */
  async getAvailableCountries(): Promise<AvailableCountriesResponse> {
    return api.get<AvailableCountriesResponse>('/assets/available-countries/')
  },

  /**
   * Get risk summary for a specific asset
   */
  async getRiskSummary(id: number): Promise<RiskSummary> {
    return api.get<RiskSummary>(`/assets/${id}/risk_summary/`)
  },

  /**
   * Search nearby assets
   */
  async getNearby(params: NearbySearchParams): Promise<NearbySearchResponse> {
    const { lat, lon, radius = 10 } = params
    return api.get<NearbySearchResponse>(
      `/assets/nearby/?lat=${lat}&lon=${lon}&radius=${radius}`
    )
  },

  /**
   * Export asset data for CAT modeling tools
   */
  async getRiskExport(): Promise<PortfolioRiskExport> {
    return api.get<PortfolioRiskExport>('/assets/risk_export/')
  },

  /**
   * Get asset statistics summary
   * @param countries Optional comma-separated country codes to filter statistics
   */
  async getStatistics(countries?: string): Promise<PortfolioStatistics> {
    const url = countries
      ? `/assets/statistics/?countries=${countries}`
      : '/assets/statistics/'
    return api.get<PortfolioStatistics>(url)
  },
}
